import React, { useCallback, useState } from 'react'
import { intl } from 'lang/intl'
import { FormattedMessage } from 'react-intl'
import { type FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { TtButton, TtIcon } from '@23/ui-react'
import * as TtForms from '@23/react-forms'

import { WebinarPanelComponent } from '../../../WebinarSidebarFeature/components'
import {
  getAudienceName,
  hasAudienceName,
} from '../../../../../Chat/blocs/useAudienceName'
import { useAskAudienceName } from '../../../../../Chat/components/AskAudienceName/AskAudienceNameModal'
import { askQuestion } from '../../../../../Chat/blocs/useQuestions'

import './WebinarQuestionsCardComponent.scss'

const validationSchema = Yup.object({
  content: Yup.string()
    .trim()
    .required(() =>
      intl.formatMessage({ defaultMessage: 'Your question cannot be empty' })
    ),
})

type FormValues = Yup.InferType<typeof validationSchema>

const initialValues: FormValues = { content: '' }

const WebinarQuestionsCardComponent = () => {
  const [isQuestionSent, setIsQuestionSent] = useState(false)

  const openAskAudienceNameModal = useAskAudienceName()

  const handleSubmit = useCallback(
    async (
      { content }: FormValues,
      { setFieldError }: FormikHelpers<FormValues>
    ) => {
      if (!hasAudienceName()) {
        const finished = await openAskAudienceNameModal()
        if (!finished) return
      }

      try {
        await askQuestion(content.trim(), getAudienceName())
        setIsQuestionSent(true)
      } catch {
        setFieldError(
          'content',
          intl.formatMessage({ defaultMessage: 'Could not send the question' })
        )
      }
    },
    [openAskAudienceNameModal]
  )

  if (isQuestionSent) {
    return (
      <div className="webinar-questions-card-component">
        <WebinarPanelComponent className="question-sent-panel">
          <span className="question-sent-message">
            <FormattedMessage defaultMessage="Question was sent and can only be seen by the hosts and speakers." />
          </span>
          <TtButton
            className="ask-new-one"
            variant="ghost"
            color="secondary"
            size="x-small"
            onClick={() => setIsQuestionSent(false)}
          >
            <FormattedMessage defaultMessage="Ask another question" />
          </TtButton>
        </WebinarPanelComponent>
      </div>
    )
  }

  return (
    <div className="webinar-questions-card-component">
      <TtForms.Form<FormValues>
        className="questions-form"
        label={intl.formatMessage({
          defaultMessage: 'Ask a question to the speakers',
        })}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <WebinarPanelComponent
            className="questions-form-panel"
            isWithoutPadding
          >
            <TtForms.TextField<FormValues>
              className="questions-form-field"
              name="content"
              label={intl.formatMessage({
                defaultMessage: 'Ask a question to the speakers',
              })}
              hideLabel
              size="medium"
              placeholder={intl.formatMessage({
                defaultMessage: 'Ask a question to the speakers',
              })}
              autocomplete="off"
            />
            <TtForms.SubmitButton
              className="questions-form-submit"
              label={intl.formatMessage({
                defaultMessage: 'Ask a question to the speakers',
              })}
              color="secondary"
              variant="light"
              size="medium"
              disabled={!formik.values.content}
              circle
            >
              <TtIcon library="one/webinars" name="send" />
            </TtForms.SubmitButton>
          </WebinarPanelComponent>
        )}
      </TtForms.Form>
    </div>
  )
}

export { WebinarQuestionsCardComponent }
